import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Trash2, Star, Ban, RotateCcw, Banknote } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner, StatusBadge, FormatBadge, EmptyState } from "@/components/common";
import { api, media, naira, fmtDate, errMsg } from "@/lib/api";

export const Filter = ({ options, value, onChange, prefix }) => (
  <div className="mb-4 flex flex-wrap gap-2">
    {options.map(([v, l]) => (
      <button key={l} onClick={() => onChange(v)} data-testid={`${prefix}-filter-${v || "all"}`}
        className={`rounded-full border px-3 py-1.5 text-xs font-medium ${value === v ? "border-primary bg-primary text-white" : "border-border"}`}>{l}</button>
    ))}
  </div>
);

function usePrompt() {
  const [state, setState] = useState(null);
  const ask = (title, placeholder, onSubmit) => setState({ title, placeholder, onSubmit, value: "" });
  const el = state && (
    <Dialog open onOpenChange={(o) => !o && setState(null)}>
      <DialogContent>
        <DialogHeader><DialogTitle>{state.title}</DialogTitle></DialogHeader>
        <Input data-testid="prompt-input" autoFocus placeholder={state.placeholder} value={state.value} onChange={(e) => setState({ ...state, value: e.target.value })} />
        <Button data-testid="prompt-submit-btn" onClick={async () => { await state.onSubmit(state.value); setState(null); }}>Submit</Button>
      </DialogContent>
    </Dialog>
  );
  return [ask, el];
}

function useList(url, params) {
  const [items, setItems] = useState(null);
  const load = () => api.get(url, { params }).then((r) => setItems(r.data));
  useEffect(() => { setItems(null); load(); }, [url, JSON.stringify(params)]); // eslint-disable-line
  return [items, load];
}

const act = async (fn, msg, reload) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };

export function AdminTemplates() {
  const [status, setStatus] = useState("pending");
  const [items, load] = useList("/admin/templates", { status });
  const [ask, prompt] = usePrompt();
  const review = (id, action, reason) => act(() => api.post(`/admin/templates/${id}/review`, { action, reason }), `Template ${action}d`, load);
  return (
    <div>
      <Filter prefix="admin-templates" value={status} onChange={setStatus} options={[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["removed", "Removed"], ["", "All"]]} />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="Nothing here" testid="admin-templates-empty" /> : (
        <div className="grid gap-3">
          {items.map((t) => (
            <div key={t.id} className="card-surface flex flex-col gap-4 p-4 md:flex-row md:items-center" data-testid={`admin-template-${t.id}`}>
              <img src={media(t.cover_image)} alt="" className="h-20 w-16 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><Link to={`/template/${t.id}`} className="font-semibold hover:text-primary">{t.title}</Link><StatusBadge s={t.status} />{t.featured && <span className="text-xs text-amber-500">★ Featured</span>}</div>
                <p className="text-xs text-muted-foreground">{t.seller_name} · {t.category_name} · {naira(t.price)} · {fmtDate(t.created_at)} · Rights confirmed: {t.copyright_confirmed ? "Yes" : "No"}</p>
                <div className="mt-1 flex gap-1">{t.file_formats.map((f) => <FormatBadge key={f} f={f} />)}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {t.status !== "approved" && <Button size="sm" onClick={() => review(t.id, "approve")} data-testid={`approve-template-${t.id}`}><Check size={14} className="mr-1" />Approve</Button>}
                {t.status === "pending" && <Button size="sm" variant="outline" onClick={() => ask("Reject template", "Reason shown to the seller", (r) => review(t.id, "reject", r))} data-testid={`reject-template-${t.id}`}><X size={14} className="mr-1" />Reject</Button>}
                {t.status === "approved" && <Button size="sm" variant="outline" onClick={() => review(t.id, "feature")} data-testid={`feature-template-${t.id}`}><Star size={14} className="mr-1" />{t.featured ? "Unfeature" : "Feature"}</Button>}
                {t.status !== "removed" && <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => ask("Remove template", "Reason (optional)", (r) => review(t.id, "remove", r))} data-testid={`remove-template-${t.id}`}><Trash2 size={14} /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {prompt}
    </div>
  );
}

export function AdminUsers() {
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [items, load] = useList("/admin/users", { role, q });
  const suspend = (u, s) => act(() => api.post(`/admin/users/${u.id}/suspend`, { suspend: s }), s ? "Account suspended" : "Account reactivated", load);
  return (
    <div>
      <div className="flex flex-wrap items-start gap-3">
        <Filter prefix="admin-users" value={role} onChange={setRole} options={[["", "All"], ["seller", "Sellers"], ["user", "Buyers"], ["admin", "Admins"]]} />
        <Input data-testid="admin-users-search" placeholder="Search name or email" className="h-9 max-w-xs" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {!items ? <Spinner /> : (
        <div className="card-surface divide-y divide-border">
          {items.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-user-${u.id}`}>
              <span className="grid h-9 w-9 place-items-center rounded-full bg-secondary font-bold">{u.name[0]}</span>
              <div className="min-w-0 flex-1"><p className="font-medium">{u.name} {u.is_seller && <span className="ml-1 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] text-primary">SELLER</span>} {u.role === "admin" && <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-500">ADMIN</span>}</p><p className="text-xs text-muted-foreground">{u.email} · joined {fmtDate(u.created_at)}</p></div>
              <StatusBadge s={u.status} />
              {u.role !== "admin" && (u.status === "suspended"
                ? <Button size="sm" variant="outline" onClick={() => suspend(u, false)} data-testid={`unsuspend-user-${u.id}`}><RotateCcw size={14} className="mr-1" />Reactivate</Button>
                : <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => suspend(u, true)} data-testid={`suspend-user-${u.id}`}><Ban size={14} className="mr-1" />Suspend</Button>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminWithdrawals() {
  const [status, setStatus] = useState("pending");
  const [items, load] = useList("/admin/withdrawals", { status });
  const [ask, prompt] = usePrompt();
  const run = (id, action, extra = {}) => act(() => api.post(`/admin/withdrawals/${id}/action`, { action, ...extra }), "Withdrawal updated", load);
  return (
    <div>
      <p className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs">No automatic payout provider is connected. Approve → send the bank transfer manually → “Mark paid” with the transfer reference.</p>
      <Filter prefix="admin-withdrawals" value={status} onChange={setStatus} options={[["pending", "Pending"], ["approved", "Approved (awaiting payout)"], ["completed", "Completed"], ["rejected", "Rejected"], ["", "All"]]} />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No withdrawals" testid="admin-withdrawals-empty" /> : (
        <div className="card-surface divide-y divide-border">
          {items.map((w) => (
            <div key={w.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-withdrawal-${w.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{naira(w.amount)} <span className="font-normal text-muted-foreground">(pay {naira(w.net_amount)} after {naira(w.fee)} fee)</span></p>
                <p className="text-xs text-muted-foreground">{w.seller_name} · {w.bank_name} · {w.account_number} · {w.account_name}</p>
                <p className="font-mono-ui text-[11px] text-muted-foreground">{w.reference} · {fmtDate(w.created_at)}{w.payout_reference ? ` · paid ref ${w.payout_reference}` : ""}</p>
              </div>
              <StatusBadge s={w.status} />
              {w.status === "pending" && <Button size="sm" onClick={() => run(w.id, "approve")} data-testid={`approve-withdrawal-${w.id}`}><Check size={14} className="mr-1" />Approve</Button>}
              {w.status === "approved" && <Button size="sm" onClick={() => ask("Mark as paid", "Bank transfer reference", (r) => run(w.id, "mark_paid", { payout_reference: r }))} data-testid={`markpaid-withdrawal-${w.id}`}><Banknote size={14} className="mr-1" />Mark paid</Button>}
              {["pending", "approved"].includes(w.status) && <Button size="sm" variant="outline" onClick={() => ask("Reject withdrawal", "Reason (refunds seller balance)", (r) => run(w.id, "reject", { note: r }))} data-testid={`reject-withdrawal-${w.id}`}><X size={14} className="mr-1" />Reject</Button>}
            </div>
          ))}
        </div>
      )}
      {prompt}
    </div>
  );
}

export function AdminReports() {
  const [status, setStatus] = useState("open");
  const [items, load] = useList("/admin/reports", { status });
  const run = (id, action, remove_template = false) => act(() => api.post(`/admin/reports/${id}/action`, { action, remove_template }), "Report updated", load);
  return (
    <div>
      <Filter prefix="admin-reports" value={status} onChange={setStatus} options={[["open", "Open"], ["resolved", "Resolved"], ["dismissed", "Dismissed"], ["", "All"]]} />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No reports" testid="admin-reports-empty" /> : (
        <div className="card-surface divide-y divide-border">
          {items.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`admin-report-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{r.reason} · <Link to={`/template/${r.template_id}`} className="text-primary">{r.template_title}</Link></p>
                <p className="text-xs text-muted-foreground">By {r.reporter_name} · {fmtDate(r.created_at)}{r.details ? ` — “${r.details}”` : ""}</p>
              </div>
              <StatusBadge s={r.status} />
              {r.status === "open" && <>
                <Button size="sm" variant="destructive" onClick={() => run(r.id, "resolve", true)} data-testid={`report-remove-${r.id}`}>Remove template</Button>
                <Button size="sm" variant="outline" onClick={() => run(r.id, "resolve")} data-testid={`report-resolve-${r.id}`}>Resolve</Button>
                <Button size="sm" variant="ghost" onClick={() => run(r.id, "dismiss")} data-testid={`report-dismiss-${r.id}`}>Dismiss</Button>
              </>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
